// PostHog does not push dashboard changes to an open browser. One refresh owner
// per SDK client keeps every mounted experiment in sync without per-hook polling.
const refreshOwners = new WeakMap();
export const EXPERIMENT_FLAG_REFRESH_MS = 60000;

function createRefreshOwner(client) {
  let timer;
  let lastRefreshAt;
  let stopReloadListener;
  const owner = { count: 0, start, stop };

  function schedule() {
    window.clearTimeout(timer);
    if (!owner.count) return;
    const remaining =
      lastRefreshAt === undefined ? 0 : lastRefreshAt + EXPERIMENT_FLAG_REFRESH_MS - Date.now();
    // A hidden tab waits for visibility or the next ordinary tick, without a
    // zero-delay loop when its last assignment becomes stale.
    timer = window.setTimeout(
      refresh,
      document.visibilityState === 'visible' ? Math.max(0, remaining) : EXPERIMENT_FLAG_REFRESH_MS,
    );
  }

  function noteReload() {
    lastRefreshAt = Date.now();
    schedule();
  }

  function refresh() {
    const stale =
      lastRefreshAt === undefined || Date.now() - lastRefreshAt >= EXPERIMENT_FLAG_REFRESH_MS;
    if (document.visibilityState === 'visible' && stale) {
      // Focus and visibility often describe the same return to a tab. SDK
      // identity/targeting reloads also satisfy this shared refresh cadence.
      noteReload();
      try {
        client.reloadFeatureFlags?.();
      } catch {
        /* Retry at the next refresh. */
      }
    }
    schedule();
  }

  function start() {
    try {
      stopReloadListener = client.on?.('featureFlagsReloading', noteReload);
    } catch {
      // The shared timer still works if an SDK listener is unavailable.
    }
    window.addEventListener('focus', refresh);
    document.addEventListener('visibilitychange', refresh);
    if (lastRefreshAt === undefined && client.featureFlags?.ensureFlagsLoaded) {
      // The SDK already owns its initial request and its in-flight work. Ask
      // that owner to ensure readiness instead of queuing a second reload.
      noteReload();
      try {
        client.featureFlags.ensureFlagsLoaded();
      } catch {
        /* Retry at the next refresh, as with an ordinary failed reload. */
      }
    }
    refresh();
  }

  function stop() {
    window.clearTimeout(timer);
    window.removeEventListener('focus', refresh);
    document.removeEventListener('visibilitychange', refresh);
    stopReloadListener?.();
  }
  return owner;
}

export function watchExperimentFlags(client, onUpdate) {
  if (!client) return () => {};
  let unsubscribe;
  try {
    unsubscribe = (client.onFeatureFlags || client.featureFlags?.onFeatureFlags)?.call(
      client.onFeatureFlags ? client : client.featureFlags,
      onUpdate,
    );
  } catch {
    // The next mount still evaluates the current SDK state and fails closed.
  }
  let owner = refreshOwners.get(client);
  if (!owner) {
    owner = createRefreshOwner(client);
    refreshOwners.set(client, owner);
  }
  owner.count += 1;
  if (owner.count === 1) owner.start();
  return () => {
    unsubscribe?.();
    owner.count -= 1;
    if (owner.count === 0) {
      owner.stop();
      // Keep freshness across route remounts. WeakMap ownership releases this
      // state with the SDK client; stopped owners retain no timers/listeners.
    }
  };
}
